import { BadgeDemo } from "./badge-demo";
import {
  FileTreeBlockDemo,
  MetricsOverviewBlockDemo,
  RepoCardBlockDemo,
  RollLogBlockDemo,
  ServerLogBlockDemo,
  StatusPageBlockDemo,
  WorkspaceShellBlockDemo,
} from "./block-demo";
import { ButtonDemo } from "./button-demo";
import { ComponentWrapper } from "./component-wrapper";
import {
  CollapsibleDemo,
  CounterDemo,
  EmptyStateDemo,
  PanelDemo,
  ScrollAreaDemo,
  SeparatorDemo,
  SkeletonDemo,
  SliderDemo,
  ToggleDemo,
  ToggleGroupDemo,
  ToolbarDemo,
} from "./controls-demo";
import {
  EnemyHealthDisplayDemo,
  HealthBarDemo,
  ItemDemo,
  ManaBarDemo,
  ResizableDemo,
  ResourceBarDemo,
  SpinnerDemo,
  ToastDemo,
  XpBarDemo,
} from "./domain-demo";
import { InputDemo } from "./input-demo";
import {
  ActionBarDemo,
  AngleSliderDemo,
  AvatarGroupDemo,
  BadgeOverflowDemo,
  BannerDemo,
  CheckboxGroupDemo,
  CircularProgressDemo,
  ColorPickerDemo,
  ColorSwatchDemo,
  ComboboxDemo,
  CompareSliderDemo,
  CropperDemo,
  DrawerDemo,
  EditableDemo,
  FileUploadDemo,
  FpsDemo,
  GaugeDemo,
  HoverCardDemo,
  KanbanDemo,
  KeyValueDemo,
  ListboxDemo,
  MarqueeDemo,
  MaskInputDemo,
  MediaPlayerDemo,
  MentionDemo,
  PhoneInputDemo,
  QrCodeDemo,
  RatingDemo,
  RelativeTimeCardDemo,
  ResponsiveDialogDemo,
  ScrollerDemo,
  ScrollSpyDemo,
  SegmentedInputDemo,
  SelectionToolbarDemo,
  SortableDemo,
  SpeedDialDemo,
  StackDemo,
  StatDemo,
  StatusDemo,
  StepperDemo,
  SwapDemo,
  TagsInputDemo,
  TextareaDemo,
  TimelineDemo,
  TimePickerDemo,
  TourDemo,
  VisuallyHiddenInputDemo,
} from "./interactive-demo";
import {
  LancerActionTrackerDemo,
  LancerCounterDemo,
  LancerMechPanelDemo,
  LancerResourceBarDemo,
  LancerStatCardDemo,
  LancerStructureStressDemo,
} from "./lancer-demo";
import { MilkdownMarkdownDemo } from "./markdown-demo";
import { CommandDemo, TabsDemo } from "./navigation-demo";
import {
  ContextMenuDemo,
  DialogDemo,
  DropdownMenuDemo,
  MenubarDemo,
  PopoverDemo,
  TooltipDemo,
} from "./overlay-demo";
import {
  ActivityGraphDemo,
  AiCopyButtonDemo,
  CIBadgeDemo,
  CodeBlockCommandDemo,
  CodeBlockDemo,
  CodeLineDemo,
  ColorPaletteDemo,
  CommitGraphDemo,
  ContributorGridDemo,
  CronScheduleDemo,
  DiffViewerDemo,
  DiscordBadgeDemo,
  DownloadSparklineDemo,
  EnvTableDemo,
  FileTreeDemo,
  GitHubButtonGroupDemo,
  GitHubStarsButtonDemo,
  JsonViewerDemo,
  KbdDemo,
  LicenseBadgeDemo,
  LogoCloudDemo,
  LogViewerDemo,
  MasonryGridDemo,
  NpmBadgeDemo,
  PretextDemo,
  ReleaseBadgeDemo,
  RepoCardDemo,
  RequestViewerDemo,
  StatusIndicatorDemo,
  TestimonialDemo,
  TipJarDemo,
} from "./product-demo";
import { SelectDemo } from "./select-demo";
import {
  ActorSheetDemo,
  ChatCardDemo,
  CommandPaletteDemo,
  CompendiumViewDemo,
  ItemSheetDemo,
  JournalKbSheetDemo,
  PartyTrackerDemo,
  RollCardDemo,
  TabletopTrackersDemo,
} from "./vtt-demo";

function SectionHeading({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="grid gap-1 pt-2">
      <h3 className="text-lg font-semibold tracking-tight">{title}</h3>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

export function Page({ origin }: { origin: string }) {
  return (
    <div className="@container grid flex-1 gap-4">
      <SectionHeading
        title="Core components"
        description="Reusable controls, overlays, and layout primitives from the shared Thilke registry."
      />
      <ComponentWrapper name="button" origin={origin}>
        <ButtonDemo />
      </ComponentWrapper>
      <ComponentWrapper name="badge" origin={origin}>
        <BadgeDemo />
      </ComponentWrapper>
      <ComponentWrapper name="counter" origin={origin}>
        <CounterDemo />
      </ComponentWrapper>
      <ComponentWrapper name="input" origin={origin}>
        <InputDemo />
      </ComponentWrapper>
      <ComponentWrapper name="select" origin={origin}>
        <SelectDemo />
      </ComponentWrapper>
      <ComponentWrapper name="dialog" origin={origin}>
        <DialogDemo />
      </ComponentWrapper>
      <ComponentWrapper name="popover" origin={origin}>
        <PopoverDemo />
      </ComponentWrapper>
      <ComponentWrapper name="dropdown-menu" origin={origin}>
        <DropdownMenuDemo />
      </ComponentWrapper>
      <ComponentWrapper name="menubar" origin={origin}>
        <MenubarDemo />
      </ComponentWrapper>
      <ComponentWrapper name="context-menu" origin={origin}>
        <ContextMenuDemo />
      </ComponentWrapper>
      <ComponentWrapper name="tooltip" origin={origin}>
        <TooltipDemo />
      </ComponentWrapper>
      <ComponentWrapper name="tabs" origin={origin}>
        <TabsDemo />
      </ComponentWrapper>
      <ComponentWrapper name="command" origin={origin}>
        <CommandDemo />
      </ComponentWrapper>
      <ComponentWrapper name="slider" origin={origin}>
        <SliderDemo />
      </ComponentWrapper>
      <ComponentWrapper name="skeleton" origin={origin}>
        <SkeletonDemo />
      </ComponentWrapper>
      <ComponentWrapper name="separator" origin={origin}>
        <SeparatorDemo />
      </ComponentWrapper>
      <ComponentWrapper name="scroll-area" origin={origin}>
        <ScrollAreaDemo />
      </ComponentWrapper>
      <ComponentWrapper name="toggle" origin={origin}>
        <ToggleDemo />
      </ComponentWrapper>
      <ComponentWrapper name="toggle-group" origin={origin}>
        <ToggleGroupDemo />
      </ComponentWrapper>
      <ComponentWrapper name="collapsible" origin={origin}>
        <CollapsibleDemo />
      </ComponentWrapper>
      <ComponentWrapper name="empty-state" origin={origin}>
        <EmptyStateDemo />
      </ComponentWrapper>
      <ComponentWrapper name="panel" origin={origin}>
        <PanelDemo />
      </ComponentWrapper>
      <ComponentWrapper name="toolbar" origin={origin}>
        <ToolbarDemo />
      </ComponentWrapper>
      <SectionHeading
        title="Interaction components"
        description="Higher-level controls for editing, selection, workboards, media, and dense operational surfaces."
      />
      <ComponentWrapper name="action-bar" origin={origin}>
        <ActionBarDemo />
      </ComponentWrapper>
      <ComponentWrapper name="angle-slider" origin={origin}>
        <AngleSliderDemo />
      </ComponentWrapper>
      <ComponentWrapper name="avatar-group" origin={origin}>
        <AvatarGroupDemo />
      </ComponentWrapper>
      <ComponentWrapper name="badge-overflow" origin={origin}>
        <BadgeOverflowDemo />
      </ComponentWrapper>
      <ComponentWrapper name="banner" origin={origin}>
        <BannerDemo />
      </ComponentWrapper>
      <ComponentWrapper name="checkbox-group" origin={origin}>
        <CheckboxGroupDemo />
      </ComponentWrapper>
      <ComponentWrapper name="circular-progress" origin={origin}>
        <CircularProgressDemo />
      </ComponentWrapper>
      <ComponentWrapper name="color-picker" origin={origin}>
        <ColorPickerDemo />
      </ComponentWrapper>
      <ComponentWrapper name="color-swatch" origin={origin}>
        <ColorSwatchDemo />
      </ComponentWrapper>
      <ComponentWrapper name="combobox" origin={origin}>
        <ComboboxDemo />
      </ComponentWrapper>
      <ComponentWrapper name="compare-slider" origin={origin}>
        <CompareSliderDemo />
      </ComponentWrapper>
      <ComponentWrapper name="cropper" origin={origin}>
        <CropperDemo />
      </ComponentWrapper>
      <ComponentWrapper name="drawer" origin={origin}>
        <DrawerDemo />
      </ComponentWrapper>
      <ComponentWrapper name="editable" origin={origin}>
        <EditableDemo />
      </ComponentWrapper>
      <ComponentWrapper name="file-upload" origin={origin}>
        <FileUploadDemo />
      </ComponentWrapper>
      <ComponentWrapper name="fps" origin={origin}>
        <FpsDemo />
      </ComponentWrapper>
      <ComponentWrapper name="gauge" origin={origin}>
        <GaugeDemo />
      </ComponentWrapper>
      <ComponentWrapper name="hover-card" origin={origin}>
        <HoverCardDemo />
      </ComponentWrapper>
      <ComponentWrapper name="kanban" origin={origin}>
        <KanbanDemo />
      </ComponentWrapper>
      <ComponentWrapper name="key-value" origin={origin}>
        <KeyValueDemo />
      </ComponentWrapper>
      <ComponentWrapper name="listbox" origin={origin}>
        <ListboxDemo />
      </ComponentWrapper>
      <ComponentWrapper name="marquee" origin={origin}>
        <MarqueeDemo />
      </ComponentWrapper>
      <ComponentWrapper name="mask-input" origin={origin}>
        <MaskInputDemo />
      </ComponentWrapper>
      <ComponentWrapper name="media-player" origin={origin}>
        <MediaPlayerDemo />
      </ComponentWrapper>
      <ComponentWrapper name="mention" origin={origin}>
        <MentionDemo />
      </ComponentWrapper>
      <ComponentWrapper name="phone-input" origin={origin}>
        <PhoneInputDemo />
      </ComponentWrapper>
      <ComponentWrapper name="qr-code" origin={origin}>
        <QrCodeDemo />
      </ComponentWrapper>
      <ComponentWrapper name="rating" origin={origin}>
        <RatingDemo />
      </ComponentWrapper>
      <ComponentWrapper name="relative-time-card" origin={origin}>
        <RelativeTimeCardDemo />
      </ComponentWrapper>
      <ComponentWrapper name="responsive-dialog" origin={origin}>
        <ResponsiveDialogDemo />
      </ComponentWrapper>
      <ComponentWrapper name="scroll-spy" origin={origin}>
        <ScrollSpyDemo />
      </ComponentWrapper>
      <ComponentWrapper name="scroller" origin={origin}>
        <ScrollerDemo />
      </ComponentWrapper>
      <ComponentWrapper name="segmented-input" origin={origin}>
        <SegmentedInputDemo />
      </ComponentWrapper>
      <ComponentWrapper name="selection-toolbar" origin={origin}>
        <SelectionToolbarDemo />
      </ComponentWrapper>
      <ComponentWrapper name="sortable" origin={origin}>
        <SortableDemo />
      </ComponentWrapper>
      <ComponentWrapper
        name="speed-dial"
        origin={origin}
        allowOverflow
        previewClassName="min-h-80 p-10"
      >
        <SpeedDialDemo />
      </ComponentWrapper>
      <ComponentWrapper
        name="stack"
        origin={origin}
        allowOverflow
        previewClassName="min-h-72 p-10"
      >
        <StackDemo />
      </ComponentWrapper>
      <ComponentWrapper name="stat" origin={origin}>
        <StatDemo />
      </ComponentWrapper>
      <ComponentWrapper name="status" origin={origin}>
        <StatusDemo />
      </ComponentWrapper>
      <ComponentWrapper name="stepper" origin={origin}>
        <StepperDemo />
      </ComponentWrapper>
      <ComponentWrapper name="swap" origin={origin}>
        <SwapDemo />
      </ComponentWrapper>
      <ComponentWrapper name="tags-input" origin={origin}>
        <TagsInputDemo />
      </ComponentWrapper>
      <ComponentWrapper name="textarea" origin={origin}>
        <TextareaDemo />
      </ComponentWrapper>
      <ComponentWrapper name="time-picker" origin={origin}>
        <TimePickerDemo />
      </ComponentWrapper>
      <ComponentWrapper name="timeline" origin={origin}>
        <TimelineDemo />
      </ComponentWrapper>
      <ComponentWrapper name="tour" origin={origin}>
        <TourDemo />
      </ComponentWrapper>
      <ComponentWrapper name="visually-hidden-input" origin={origin}>
        <VisuallyHiddenInputDemo />
      </ComponentWrapper>
      <SectionHeading
        title="Components"
        description="Canonical components from the shared Thilke registry."
      />{" "}
      <ComponentWrapper name="resource-bar" origin={origin}>
        <ResourceBarDemo />
      </ComponentWrapper>{" "}
      <ComponentWrapper name="enemy-health-display" origin={origin}>
        <EnemyHealthDisplayDemo />
      </ComponentWrapper>{" "}
      <ComponentWrapper name="health-bar" origin={origin}>
        <HealthBarDemo />
      </ComponentWrapper>{" "}
      <ComponentWrapper name="item" origin={origin}>
        <ItemDemo />
      </ComponentWrapper>{" "}
      <ComponentWrapper name="mana-bar" origin={origin}>
        <ManaBarDemo />
      </ComponentWrapper>{" "}
      <ComponentWrapper name="resizable" origin={origin}>
        <ResizableDemo />
      </ComponentWrapper>{" "}
      <ComponentWrapper name="spinner" origin={origin}>
        <SpinnerDemo />
      </ComponentWrapper>{" "}
      <ComponentWrapper name="toast" origin={origin}>
        <ToastDemo />
      </ComponentWrapper>{" "}
      <ComponentWrapper name="xp-bar" origin={origin}>
        <XpBarDemo />
      </ComponentWrapper>{" "}
      <ComponentWrapper name="actor-sheet" origin={origin} allowOverflow>
        <ActorSheetDemo />
      </ComponentWrapper>
      <ComponentWrapper name="item-sheet" origin={origin} allowOverflow>
        <ItemSheetDemo />
      </ComponentWrapper>
      <ComponentWrapper name="chat-card" origin={origin}>
        <ChatCardDemo />
      </ComponentWrapper>
      <ComponentWrapper name="roll-card" origin={origin} allowOverflow>
        <RollCardDemo />
      </ComponentWrapper>
      <ComponentWrapper
        name="tabletop-trackers"
        origin={origin}
        allowOverflow
        previewClassName="min-h-72 p-6"
      >
        <TabletopTrackersDemo />
      </ComponentWrapper>
      <ComponentWrapper name="compendium-view" origin={origin} allowOverflow>
        <CompendiumViewDemo />
      </ComponentWrapper>
      <ComponentWrapper name="party-tracker" origin={origin} allowOverflow>
        <PartyTrackerDemo />
      </ComponentWrapper>
      <ComponentWrapper name="journal-kb-sheet" origin={origin} allowOverflow>
        <JournalKbSheetDemo />
      </ComponentWrapper>
      <ComponentWrapper name="command-palette" origin={origin}>
        <CommandPaletteDemo />
      </ComponentWrapper>
      <SectionHeading
        title="Lancer"
        description="Mechanical, tactile components styled for the foundry.thilke Lancer instance."
      />
      <ComponentWrapper name="lancer-counter" origin={origin}>
        <LancerCounterDemo />
      </ComponentWrapper>
      <ComponentWrapper name="lancer-resource-bar" origin={origin}>
        <LancerResourceBarDemo />
      </ComponentWrapper>
      <ComponentWrapper name="lancer-stat-card" origin={origin}>
        <LancerStatCardDemo />
      </ComponentWrapper>
      <ComponentWrapper name="lancer-action-tracker" origin={origin}>
        <LancerActionTrackerDemo />
      </ComponentWrapper>
      <ComponentWrapper name="lancer-structure-stress" origin={origin}>
        <LancerStructureStressDemo />
      </ComponentWrapper>
      <ComponentWrapper name="lancer-mech-panel" origin={origin}>
        <LancerMechPanelDemo />
      </ComponentWrapper>
      <SectionHeading
        title="Data, code, and publishing"
        description="Cards, badges, viewers, logs, repository widgets, and content-heavy components."
      />
      <ComponentWrapper name="code-block" origin={origin}>
        <CodeBlockDemo />
      </ComponentWrapper>
      <ComponentWrapper name="code-block-command" origin={origin}>
        <CodeBlockCommandDemo />
      </ComponentWrapper>
      <ComponentWrapper name="code-line" origin={origin}>
        <CodeLineDemo />
      </ComponentWrapper>
      <ComponentWrapper name="diff-viewer" origin={origin}>
        <DiffViewerDemo />
      </ComponentWrapper>
      <ComponentWrapper
        name="milkdown-markdown"
        origin={origin}
        previewClassName="items-stretch justify-stretch"
      >
        <MilkdownMarkdownDemo />
      </ComponentWrapper>
      <ComponentWrapper name="ai-copy-button" origin={origin}>
        <AiCopyButtonDemo />
      </ComponentWrapper>
      <ComponentWrapper name="color-palette" origin={origin}>
        <ColorPaletteDemo />
      </ComponentWrapper>
      <ComponentWrapper name="file-tree" origin={origin}>
        <FileTreeDemo />
      </ComponentWrapper>
      <ComponentWrapper name="kbd" origin={origin}>
        <KbdDemo />
      </ComponentWrapper>
      <ComponentWrapper name="activity-graph" origin={origin}>
        <ActivityGraphDemo />
      </ComponentWrapper>
      <ComponentWrapper name="ci-badge" origin={origin}>
        <CIBadgeDemo />
      </ComponentWrapper>
      <ComponentWrapper name="commit-graph" origin={origin}>
        <CommitGraphDemo />
      </ComponentWrapper>
      <ComponentWrapper name="contributor-grid" origin={origin}>
        <ContributorGridDemo />
      </ComponentWrapper>
      <ComponentWrapper name="github-button-group" origin={origin}>
        <GitHubButtonGroupDemo />
      </ComponentWrapper>
      <ComponentWrapper name="github-stars-button" origin={origin}>
        <GitHubStarsButtonDemo />
      </ComponentWrapper>
      <ComponentWrapper name="release-badge" origin={origin}>
        <ReleaseBadgeDemo />
      </ComponentWrapper>
      <ComponentWrapper name="repo-card" origin={origin}>
        <RepoCardDemo />
      </ComponentWrapper>
      <ComponentWrapper name="discord-badge" origin={origin}>
        <DiscordBadgeDemo />
      </ComponentWrapper>
      <ComponentWrapper name="download-sparkline" origin={origin}>
        <DownloadSparklineDemo />
      </ComponentWrapper>
      <ComponentWrapper name="license-badge" origin={origin}>
        <LicenseBadgeDemo />
      </ComponentWrapper>
      <ComponentWrapper name="npm-badge" origin={origin}>
        <NpmBadgeDemo />
      </ComponentWrapper>
      <ComponentWrapper name="cron-schedule" origin={origin}>
        <CronScheduleDemo />
      </ComponentWrapper>
      <ComponentWrapper name="env-table" origin={origin}>
        <EnvTableDemo />
      </ComponentWrapper>
      <ComponentWrapper name="json-viewer" origin={origin}>
        <JsonViewerDemo />
      </ComponentWrapper>
      <ComponentWrapper name="log-viewer" origin={origin}>
        <LogViewerDemo />
      </ComponentWrapper>
      <ComponentWrapper name="request-viewer" origin={origin}>
        <RequestViewerDemo />
      </ComponentWrapper>
      <ComponentWrapper name="logo-cloud" origin={origin}>
        <LogoCloudDemo />
      </ComponentWrapper>
      <ComponentWrapper name="testimonial" origin={origin}>
        <TestimonialDemo />
      </ComponentWrapper>
      <ComponentWrapper name="status-indicator" origin={origin}>
        <StatusIndicatorDemo />
      </ComponentWrapper>
      <ComponentWrapper name="tip-jar" origin={origin}>
        <TipJarDemo />
      </ComponentWrapper>
      <ComponentWrapper name="pretext" origin={origin}>
        <PretextDemo />
      </ComponentWrapper>
      <ComponentWrapper name="masonry-grid" origin={origin}>
        <MasonryGridDemo />
      </ComponentWrapper>
      <SectionHeading
        title="Blocks"
        description="Composed sections for dashboards, logs, repositories, file trees, and status pages."
      />
      <ComponentWrapper name="workspace-shell" origin={origin}>
        <WorkspaceShellBlockDemo />
      </ComponentWrapper>
      <ComponentWrapper name="metrics-overview-block" origin={origin}>
        <MetricsOverviewBlockDemo />
      </ComponentWrapper>
      <ComponentWrapper name="server-log-block" origin={origin}>
        <ServerLogBlockDemo />
      </ComponentWrapper>
      <ComponentWrapper name="roll-log-block" origin={origin}>
        <RollLogBlockDemo />
      </ComponentWrapper>
      <ComponentWrapper name="repo-card-block" origin={origin}>
        <RepoCardBlockDemo />
      </ComponentWrapper>
      <ComponentWrapper name="file-tree-block" origin={origin}>
        <FileTreeBlockDemo />
      </ComponentWrapper>
      <ComponentWrapper name="status-page-block" origin={origin}>
        <StatusPageBlockDemo />
      </ComponentWrapper>
    </div>
  );
}
